import React, { useState, useEffect } from 'react';
import { BarChart3, Eye, Search, MessageSquare, TrendingUp, Crown, Lock } from 'lucide-react';
import axios from 'axios';

import { BACKEND_URL } from '../config/api';

export default function ProfileAnalytics({ subscription, onUpgrade, monetizationEnabled = false }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d');

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/tradespeople/analytics`, {
        withCredentials: true
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  const isPremium = analytics?.subscription_tier === 'premium' || analytics?.subscription_tier === 'pro';

  const getMetricValue = (metric) => {
    if (!analytics) return 0;
    return timeRange === '7d' 
      ? analytics[metric]?.last_7_days 
      : analytics[metric]?.last_30_days;
  };

  const getPercentChange = (current, previous) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  if (loading) {
    return (
      <div className="card" data-testid="analytics-loading">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-slate-200 rounded w-1/3"></div>
          <div className="grid grid-cols-3 gap-4">
            <div className="h-24 bg-slate-200 rounded"></div>
            <div className="h-24 bg-slate-200 rounded"></div>
            <div className="h-24 bg-slate-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card" data-testid="profile-analytics">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-orange-500" />
          Profile Analytics
        </h3>
        <div className="flex items-center gap-2">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="text-sm border border-slate-200 rounded-lg px-3 py-1.5"
            data-testid="time-range-select"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </select>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Profile Views */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200" data-testid="metric-profile-views">
          <div className="flex items-center justify-between mb-2">
            <Eye className="h-5 w-5 text-blue-600" />
            <span className="text-xs text-blue-600 font-medium">{timeRange === '7d' ? '7 days' : '30 days'}</span>
          </div>
          <p className="text-3xl font-bold text-blue-900">{getMetricValue('profile_views')}</p>
          <p className="text-sm text-blue-700">Profile Views</p>
          <p className="text-xs text-blue-500 mt-1">Total: {analytics?.profile_views?.total || 0}</p>
        </div>

        {/* Search Appearances */}
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200" data-testid="metric-search-appearances">
          <div className="flex items-center justify-between mb-2">
            <Search className="h-5 w-5 text-purple-600" />
            <span className="text-xs text-purple-600 font-medium">{timeRange === '7d' ? '7 days' : '30 days'}</span>
          </div>
          <p className="text-3xl font-bold text-purple-900">{getMetricValue('search_appearances')}</p>
          <p className="text-sm text-purple-700">Search Appearances</p>
          <p className="text-xs text-purple-500 mt-1">Total: {analytics?.search_appearances?.total || 0}</p>
        </div>

        {/* Messages Received */}
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200" data-testid="metric-messages">
          <div className="flex items-center justify-between mb-2">
            <MessageSquare className="h-5 w-5 text-green-600" />
            <span className="text-xs text-green-600 font-medium">{timeRange === '7d' ? '7 days' : '30 days'}</span>
          </div>
          <p className="text-3xl font-bold text-green-900">{getMetricValue('messages_received')}</p>
          <p className="text-sm text-green-700">Messages Received</p>
          <p className="text-xs text-green-500 mt-1">Total: {analytics?.messages_received?.total || 0}</p>
        </div>
      </div>

      {/* Daily Chart - Premium Feature */}
      {isPremium ? (
        <div className="border border-slate-200 rounded-xl p-4" data-testid="daily-chart">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-4 w-4 text-orange-500" />
            <h4 className="font-semibold text-slate-900">Daily Breakdown</h4>
            <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full font-medium">
              {analytics?.subscription_tier?.toUpperCase()}
            </span>
          </div>
          
          {/* Simple Bar Chart */}
          <div className="space-y-4">
            <div>
              <p className="text-sm text-slate-600 mb-2">Profile Views (Last 7 Days)</p>
              <div className="flex items-end gap-1 h-16">
                {analytics?.profile_views?.daily?.map((day, idx) => {
                  const maxVal = Math.max(...(analytics?.profile_views?.daily?.map(d => d.count) || [1]), 1);
                  const height = (day.count / maxVal) * 100;
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center">
                      <div 
                        className="w-full bg-blue-500 rounded-t transition-all hover:bg-blue-600"
                        style={{ height: `${Math.max(height, 5)}%` }}
                        title={`${day.date}: ${day.count} views`}
                      />
                      <span className="text-xs text-slate-400 mt-1">
                        {new Date(day.date).toLocaleDateString('en-GB', { weekday: 'short' }).charAt(0)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div>
              <p className="text-sm text-slate-600 mb-2">Search Appearances (Last 7 Days)</p>
              <div className="flex items-end gap-1 h-16">
                {analytics?.search_appearances?.daily?.map((day, idx) => {
                  const maxVal = Math.max(...(analytics?.search_appearances?.daily?.map(d => d.count) || [1]), 1);
                  const height = (day.count / maxVal) * 100;
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center">
                      <div 
                        className="w-full bg-purple-500 rounded-t transition-all hover:bg-purple-600"
                        style={{ height: `${Math.max(height, 5)}%` }}
                        title={`${day.date}: ${day.count} appearances`}
                      />
                      <span className="text-xs text-slate-400 mt-1">
                        {new Date(day.date).toLocaleDateString('en-GB', { weekday: 'short' }).charAt(0)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      ) : monetizationEnabled ? (
        <div 
          className="border border-slate-200 rounded-xl p-6 bg-slate-50 text-center cursor-pointer hover:border-orange-300 transition-all"
          onClick={onUpgrade}
          data-testid="upgrade-prompt"
        >
          <div className="flex items-center justify-center gap-2 mb-3">
            <Lock className="h-5 w-5 text-slate-400" />
            <h4 className="font-semibold text-slate-700">Daily Analytics Breakdown</h4>
          </div>
          <p className="text-sm text-slate-600 mb-4">
            Upgrade to Premium or Pro to see detailed daily charts and track your profile performance over time.
          </p>
          <button className="btn-accent text-sm flex items-center gap-2 mx-auto">
            <Crown className="h-4 w-4" />
            Unlock with Premium
          </button>
        </div>
      ) : null}

      {/* Tips */}
      <div className="mt-6 p-4 bg-orange-50 border border-orange-200 rounded-xl">
        <h4 className="font-semibold text-orange-900 mb-2">Tips to Boost Your Profile</h4>
        <ul className="text-sm text-orange-800 space-y-1">
          <li>• Complete all profile fields to appear in more searches</li>
          <li>• Submit credentials for verification to build trust with employers</li>
          <li>• Keep your availability calendar up to date</li>
          {monetizationEnabled && !isPremium && (
            <li className="font-medium">• Upgrade to Premium for priority search placement</li>
          )}
        </ul>
      </div>
    </div>
  );
}
